import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { backendUrl } from "../utils/urlHelper";
import { isLoggedIn } from "../utils/authHelper";

const StudentAdmissionForm = ({ teacherName }) => {
  const navigate = useNavigate();

  const [studentName, setStudentName] = useState("");
  const [grade, setGrade] = useState("");
  const [currentSchool, setCurrentSchool] = useState("");
  const [parentDetails, setParentDetails] = useState([]);
  const [address, setAddress] = useState("");
  const [village, setVillage] = useState("");
  const [hasSiblings, setHasSiblings] = useState(false);
  const [siblings, setSiblings] = useState([]);
  const [referenceType, setReferenceType] = useState("");
  const [leadType, setLeadType] = useState("");
  const [errors, setErrors] = useState({});

  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [locationPermission, setLocationPermission] = useState("pending");
  const [openLocationDialog, setOpenLocationDialog] = useState(false);

  const loggedIn = isLoggedIn();
  const LOCATION_SKIP_KEY = "jass_adm_loc_skip";

  // Helper function to check if a value is empty
  const isEmpty = (value) => value.trim() === "";

  // Validate the form
  const validate = () => {
    const newErrors = {};

    // Student Info Validation
    if (isEmpty(studentName))
      newErrors.studentName = "Student Name is required";
    if (isEmpty(grade)) newErrors.grade = "Class/Grade is required";
    if (isEmpty(currentSchool)) newErrors.currentSchool = "School is required";

    // Parent Details Validation
    if (parentDetails.length === 0) {
      newErrors.parentDetails = "At least one parent/guardian is required";
    } else {
      parentDetails.forEach((parent, index) => {
        if (isEmpty(parent.relationship))
          newErrors[`parentRelationship${index}`] = "Relationship is required";
        if (isEmpty(parent.name))
          newErrors[`parentName${index}`] = "Name is required";
        if (isEmpty(parent.mobile) || !/^\d{10}$/.test(parent.mobile))
          newErrors[`parentMobile${index}`] =
            "Valid 10-digit mobile number is required";
        if (isEmpty(parent.occupation))
          newErrors[`parentOccupation${index}`] = "Occupation is required";
      });
    }

    // Location Validation
    if (isEmpty(address)) newErrors.address = "Address is required";
    if (isEmpty(village)) newErrors.village = "Village/Area Name is required";

    // Sibling Validation
    if (hasSiblings) {
      siblings.forEach((sibling, index) => {
        if (isEmpty(sibling.name))
          newErrors[`siblingName${index}`] = "Sibling Name is required";
        if (isEmpty(sibling.grade))
          newErrors[`siblingGrade${index}`] = "Class/Grade is required";
        if (isEmpty(sibling.school))
          newErrors[`siblingSchool${index}`] = "School is required";
      });
    }

    // Marketing Section Validation
    if (isEmpty(referenceType))
      newErrors.referenceType = "Reference Type is required";
    if (isEmpty(leadType)) newErrors.leadType = "Lead Type is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submitEnquiry = async (formData) => {
    try {
      //check if enquiry submitted by staff or open user
      const user = localStorage.getItem("user")
        ? JSON.parse(localStorage.getItem("user"))
        : {};
      let uri = "";
      if (user?.role) {
        uri = "/api/teacher/enquiry";
      } else {
        uri = "/api/enquiries";
      }

      const token = localStorage.getItem("token"); // Retrieve token from local storage

      const response = await axios.post(backendUrl + uri, formData, {
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      });

      console.log("Enquiry submitted successfully:", response.data);
      return response.data; // Return response data for further use
    } catch (error) {
      console.error(
        "Failed to submit enquiry:",
        error.response?.data?.message || error.message
      );
      throw error; // Rethrow error for handling in UI
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loggedIn && locationPermission !== "granted") {
      const proceedWithoutLocation = window.confirm(
        "You have not granted location permission. Are you sure you want to proceed without location?"
      );
      if (!proceedWithoutLocation) {
        // User canceled, so abort form submission
        return;
      }
    }

    if (validate()) {
      const formData = {
        studentInfo: {
          fullName: studentName,
          currentGrade: grade,
          currentSchool: currentSchool,
        },
        parents: parentDetails,
        location: {
          address: address,
          village: village,
          coordinates: {
            latitude: latitude,
            longitude: longitude,
          },
        },
        marketing: {
          referenceType: referenceType,
          leadType: leadType,
        },
        siblings: siblings,
      };

      try {
        const response = await submitEnquiry(formData);
        alert("Enquiry submitted successfully!");
        navigate(-1);
      } catch (error) {
        alert(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to submit enquiry. Please try again."
        );
      }
    }
  };

  // Add parent dynamically
  const addParent = () => {
    setParentDetails([
      ...parentDetails,
      { relationship: "", name: "", mobile: "", occupation: "" },
    ]);
  };

  // Remove parent dynamically
  const removeParent = (index) => {
    setParentDetails(parentDetails.filter((_, idx) => idx !== index));
  };

  // Add sibling dynamically
  const addSibling = () => {
    setSiblings([...siblings, { name: "", grade: "", school: "" }]);
  };

  // Remove sibling dynamically
  const removeSibling = (index) => {
    setSiblings(siblings.filter((_, idx) => idx !== index));
  };

  const handleGrantLocation = () => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLatitude(position.coords.latitude);
          setLongitude(position.coords.longitude);
          setLocationPermission("granted");
          setOpenLocationDialog(false);
        },
        (error) => {
          // Handle denial/error
          if (error.code === error.PERMISSION_DENIED) {
            setLocationPermission("denied");
          } else {
            setLocationPermission("error");
          }
        },
        { enableHighAccuracy: true }
      );
    } else {
      setLocationPermission("not_supported");
    }
  };

  const handleCloseLocationDialog = () => {
    // If user cancels, set permission to 'denied' or something
    sessionStorage.setItem(LOCATION_SKIP_KEY, "1");
    setLocationPermission("denied");
    setOpenLocationDialog(false);
  };

  const checkAndFetchLocation = () => {
    if (sessionStorage.getItem(LOCATION_SKIP_KEY) === "1") return;

    if (navigator.permissions?.query) {
      navigator.permissions
        .query({ name: "geolocation" })
        .then((res) => {
          if (res.state === "granted") {
            handleGrantLocation(); // will not prompt
          } else {
            // not granted -> do nothing for guests, show dialog for logged-in
            if (loggedIn) setOpenLocationDialog(true);
          }
        })
        .catch(() => {
          // if permission check fails:
          if (loggedIn) setOpenLocationDialog(true);
        });
    } else {
      // Permissions API not supported:
      // For guests: do nothing (avoid triggering prompt)
      // For logged-in: you can show dialog
      if (loggedIn) setOpenLocationDialog(true);
    }
  };

  useEffect(() => {
    checkAndFetchLocation();
  }, []);

  return (
    <Container
      sx={{
        marginTop: 4,
        maxWidth: { xs: "100%", sm: "100%", md: "800px", lg: "800px" },
      }}
    >
      <Typography variant="h5" className="text-blue-900" sx={{ mb: 3 }}>
        JASS Admissions | New Admission Enquiry
      </Typography>

      {!loggedIn && (
        <Box
          sx={{
            mb: 3,
            p: 2,
            borderRadius: 2,
            backgroundColor: "#fff3cd",
            border: "1px solid #ffe69c",
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            You are not logged in.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            This enquiry will be submitted as a guest enquiry. If you are a
            staff member, please log in first so the enquiry is linked to your
            account.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate("/login")}
          >
            Login
          </Button>
        </Box>
      )}

      {/* Student Info Section */}
      <Box
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          padding: 2,
          marginBottom: 4,
        }}
      >
        <Typography variant="h6" sx={{ marginBottom: 2 }}>
          Student Info
        </Typography>
        <TextField
          label="Student Full Name"
          variant="outlined"
          fullWidth
          sx={{ marginBottom: 2 }}
          value={studentName}
          onChange={(e) => setStudentName(e.target.value)}
          error={!!errors.studentName}
          helperText={errors.studentName}
        />
        <FormControl fullWidth sx={{ marginBottom: 2 }} error={!!errors.grade}>
          <InputLabel>Current Class/Grade</InputLabel>
          <Select
            label="Current Class/Grade"
            value={grade}
            onChange={(e) => setGrade(e.target.value)}
          >
            <MenuItem value="Nursery">Nursery</MenuItem>
            <MenuItem value="LKG">LKG</MenuItem>
            <MenuItem value="UKG">UKG</MenuItem>
            {[...Array(10).keys()].map((i) => (
              <MenuItem key={i} value={`Class ${i + 1}`}>
                Class {i + 1}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Current School"
          variant="outlined"
          fullWidth
          value={currentSchool}
          onChange={(e) => setCurrentSchool(e.target.value)}
          error={!!errors.currentSchool}
          helperText={errors.currentSchool}
        />
      </Box>

      {/* Parent/Guardian Details Section */}
      <Box
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          padding: 2,
          marginBottom: 4,
        }}
      >
        <Typography variant="h6" sx={{ marginBottom: 2 }}>
          Parents/Guardian Details
        </Typography>
        {parentDetails.map((parent, index) => (
          <Box key={index} sx={{ marginBottom: 3 }}>
            <FormControl
              fullWidth
              sx={{ marginBottom: 2 }}
              error={!!errors[`parentRelationship${index}`]}
            >
              <InputLabel>Relationship</InputLabel>
              <Select
                label="Relationship"
                value={parent.relationship}
                onChange={(e) =>
                  setParentDetails(
                    parentDetails.map((p, idx) =>
                      idx === index ? { ...p, relationship: e.target.value } : p
                    )
                  )
                }
              >
                <MenuItem value="Father">Father</MenuItem>
                <MenuItem value="Mother">Mother</MenuItem>
                <MenuItem value="Guardian">Guardian</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Name"
              variant="outlined"
              fullWidth
              sx={{ marginBottom: 2 }}
              value={parent.name}
              onChange={(e) =>
                setParentDetails(
                  parentDetails.map((p, idx) =>
                    idx === index ? { ...p, name: e.target.value } : p
                  )
                )
              }
              error={!!errors[`parentName${index}`]}
              helperText={errors[`parentName${index}`]}
            />
            <TextField
              label="Mobile Number"
              variant="outlined"
              fullWidth
              sx={{ marginBottom: 2 }}
              value={parent.mobile}
              onChange={(e) =>
                setParentDetails(
                  parentDetails.map((p, idx) =>
                    idx === index ? { ...p, mobile: e.target.value } : p
                  )
                )
              }
              error={!!errors[`parentMobile${index}`]}
              helperText={errors[`parentMobile${index}`]}
            />
            <TextField
              label="Occupation"
              variant="outlined"
              fullWidth
              value={parent.occupation}
              onChange={(e) =>
                setParentDetails(
                  parentDetails.map((p, idx) =>
                    idx === index ? { ...p, occupation: e.target.value } : p
                  )
                )
              }
              error={!!errors[`parentOccupation${index}`]}
              helperText={errors[`parentOccupation${index}`]}
            />
            <Button
              variant="outlined"
              color="error"
              sx={{ marginTop: 1 }}
              onClick={() => removeParent(index)}
            >
              Remove Parent/Guardian
            </Button>
          </Box>
        ))}
        <Button variant="contained" onClick={addParent}>
          Add Parent/Guardian
        </Button>
        {errors.parentDetails && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {errors.parentDetails}
          </Typography>
        )}
      </Box>

      {/* Other Sections */}
      {/* Location Section */}
      <Box
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          padding: 2,
          marginBottom: 1,
        }}
      >
        <Typography variant="h6" sx={{ marginBottom: 2 }}>
          Location Details
        </Typography>
        <TextField
          label="Address"
          variant="outlined"
          fullWidth
          sx={{ marginBottom: 2 }}
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          error={!!errors.address}
          helperText={errors.address}
        />
        <TextField
          label="Village/Area Name"
          variant="outlined"
          fullWidth
          value={village}
          onChange={(e) => setVillage(e.target.value)}
          error={!!errors.village}
          helperText={errors.village}
        />
      </Box>

      {/* Sibling Section */}
      <FormControlLabel
        sx={{ my: 2, ml: 1 }}
        control={
          <Checkbox
            checked={hasSiblings}
            onChange={(e) => setHasSiblings(e.target.checked)}
          />
        }
        label="Has Siblings?"
      />
      {hasSiblings && (
        <Box
          sx={{
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            padding: 2,
            marginBottom: 4,
          }}
        >
          <Typography variant="h6">Sibling Details</Typography>
          {siblings.map((sibling, index) => (
            <Box key={index} sx={{ marginBottom: 3 }}>
              <TextField
                label="Sibling Full Name"
                variant="outlined"
                fullWidth
                sx={{ marginBottom: 2 }}
                value={sibling.name}
                onChange={(e) =>
                  setSiblings(
                    siblings.map((s, idx) =>
                      idx === index ? { ...s, name: e.target.value } : s
                    )
                  )
                }
                error={!!errors[`siblingName${index}`]}
                helperText={errors[`siblingName${index}`]}
              />
              <FormControl fullWidth sx={{ marginBottom: 2 }}>
                <InputLabel>Current Class/Grade</InputLabel>
                <Select
                  label="Current Class/Grade"
                  value={sibling.grade}
                  onChange={(e) =>
                    setSiblings(
                      siblings.map((s, idx) =>
                        idx === index ? { ...s, grade: e.target.value } : s
                      )
                    )
                  }
                >
                  <MenuItem value="Nursery">Nursery</MenuItem>
                  <MenuItem value="LKG">LKG</MenuItem>
                  <MenuItem value="UKG">UKG</MenuItem>
                  {[...Array(10).keys()].map((i) => (
                    <MenuItem key={i + 1} value={`Class ${i + 1}`}>
                      Class {i + 1}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Current School"
                variant="outlined"
                fullWidth
                sx={{ marginBottom: 2 }}
                value={sibling.school}
                onChange={(e) =>
                  setSiblings(
                    siblings.map((s, idx) =>
                      idx === index ? { ...s, school: e.target.value } : s
                    )
                  )
                }
                error={!!errors[`siblingSchool${index}`]}
                helperText={errors[`siblingSchool${index}`]}
              />
              <Button
                variant="outlined"
                color="error"
                sx={{ marginTop: 1 }}
                onClick={() => removeSibling(index)}
              >
                Remove Sibling
              </Button>
            </Box>
          ))}
          <Button variant="contained" onClick={addSibling}>
            Add Sibling
          </Button>
        </Box>
      )}

      {/* Marketing Section */}
      <Box
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          padding: 2,
          marginBottom: 2,
        }}
      >
        <Typography variant="h6" sx={{ marginBottom: 2 }}>
          Enquiry Details
        </Typography>
        <FormControl fullWidth sx={{ marginBottom: 2 }}>
          <InputLabel>Enquired via</InputLabel>
          <Select
            label="Enquired via"
            value={referenceType}
            onChange={(e) => setReferenceType(e.target.value)}
            error={!!errors.referenceType}
          >
            <MenuItem value="walk-in">Walk-In</MenuItem>
            <MenuItem value="doorstep">Doorstep</MenuItem>
            <MenuItem value="word-of-mouth">Word of Mouth</MenuItem>
            <MenuItem value="advertisement">Advertisement</MenuItem>
            <MenuItem value="parent referral">Parent Referral</MenuItem>
            <MenuItem value="sibling">Sibling</MenuItem>
            <MenuItem value="others">Others</MenuItem>
          </Select>
          {errors.referenceType && (
            <Typography variant="body2" color="error">
              {errors.referenceType}
            </Typography>
          )}
        </FormControl>
        <FormControl fullWidth>
          <InputLabel>Enquiry Type</InputLabel>
          <Select
            label="Enquiry Type"
            value={leadType}
            onChange={(e) => setLeadType(e.target.value)}
            error={!!errors.leadType}
          >
            <MenuItem value="cold">Cold</MenuItem>
            <MenuItem value="warm">Warm</MenuItem>
            <MenuItem value="hot">Hot</MenuItem>
          </Select>
          {errors.leadType && (
            <Typography variant="body2" color="error">
              {errors.leadType}
            </Typography>
          )}
        </FormControl>
      </Box>

      <Box sx={{ my: 4, mb: 6 }}>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          sx={{ my: 2 }}
          onClick={handleSubmit}
        >
          {loggedIn ? "Submit" : "Submit as Guest"}
        </Button>
        <Button
          variant="outlined"
          color="primary"
          fullWidth
          sx={{ my: 2 }}
          onClick={() => navigate(-1)}
        >
          Go Back
        </Button>
      </Box>

      {/* Location Dialog */}
      <Dialog open={openLocationDialog}>
        <DialogTitle>Location Required</DialogTitle>
        <DialogContent>
          <Typography>
            This form requires your location access to proceed. Please grant
            location access to continue. If you are unable to grant location,
            contact Admin team.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ my: 1 }}>
          <Button variant="contained" onClick={handleGrantLocation}>
            Grant Location
          </Button>
          <Button variant="outlined" onClick={handleCloseLocationDialog}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default StudentAdmissionForm;
